import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Wrench, Plus, RefreshCw, Loader2, Copy, ChevronRight, ArrowLeft,
  Mail, Phone, MessageSquare, Trash2, CheckCircle2, AlertCircle, Code2, Eye, X, Pencil, Save,
  Activity, Sparkles, MessagesSquare, ChevronDown, MapPin, Globe, Send, Search, Users
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { API_URL } from "../utils/api";
import { kundfas, widgetStatus, runtimeState } from "./clients/statusHelpers";

const DEFAULT_PRICING_TEMPLATE = `{
  "timpris": 650,
  "altan": 1500,
  "kakelsättning_per_m2": 950
}`;


export function AddClientForm({ onAdded, onCancel }) {
  const { getAuthHeaders } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [city, setCity] = useState("");
  const [allowedDomain, setAllowedDomain] = useState("");
  const [companyUrl, setCompanyUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [priorityFieldsText, setPriorityFieldsText] = useState("");
  const [proactiveMessage, setProactiveMessage] = useState("");
  const [proactiveDelayMs, setProactiveDelayMs] = useState("5000");
  const [pricingText, setPricingText] = useState(DEFAULT_PRICING_TEMPLATE);
  const [submitting, setSubmitting] = useState(false);
  const [pricingError, setPricingError] = useState("");
  // v3.44: parity with EditClientModal — these were missing from add-form
  // so admin had to reopen "Redigera klient" to set them.
  const [tone, setTone] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [startMessage, setStartMessage] = useState("");
  const [streamingEnabled, setStreamingEnabled] = useState(false);
  const [streamingWordDelayMs, setStreamingWordDelayMs] = useState("160");
  const [active, setActive] = useState(true);
  // Demo state — auto-enabled when AI-fyll runs so the admin gets a
  // working /demo/chatbot/{slug} link from the moment they save.
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [demoSandbox, setDemoSandbox] = useState(false);
  const [demoSlug, setDemoSlug] = useState("");
  const [demoStatus, setDemoStatus] = useState("demo");
  const [demoHeadline, setDemoHeadline] = useState("");
  const [demoDescription, setDemoDescription] = useState("");
  const [demoTestQuestions, setDemoTestQuestions] = useState("");
  const [demoScreenshotUrl, setDemoScreenshotUrl] = useState("");

  // ── AI autofill via scrape (same pipeline /skapa-ai-agent uses) ──
  const [aiUrl, setAiUrl] = useState("");
  const [aiState, setAiState] = useState("idle"); // idle | scraping | ok | error
  const [aiError, setAiError] = useState("");
  // Fas 4 — enat skapandeflöde: när AI-fyll körts skapas kunden via hela
  // kedjan (draft → promote) som bygger Blueprint + föreslagna actions + demo.
  const [aiDraftId, setAiDraftId] = useState("");

  const runAiAutofill = async () => {
    const raw = aiUrl.trim();
    if (!raw) {
      setAiError("Skriv in en URL först");
      return;
    }
    setAiState("scraping");
    setAiError("");
    try {
      // 1) Start draft analysis (admin variant — owned by current admin)
      const startRes = await fetch(`${API_URL}/api/admin/onboarding/drafts/from-url`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ url: raw }),
      });
      const startData = await startRes.json();
      if (!startRes.ok || !startData.success) {
        throw new Error(startData.detail || "Kunde inte starta AI-analys");
      }
      const draftId = startData.draft_id;
      setAiDraftId(draftId);
      // 2) Poll the draft until status leaves "analyzing" (max ~45s)
      const deadline = Date.now() + 45000;
      let draft = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        const pollRes = await fetch(
          `${API_URL}/api/admin/onboarding/drafts/${draftId}`,
          { credentials: "include", headers: { ...getAuthHeaders() } }
        );
        if (!pollRes.ok) continue;
        const pollData = await pollRes.json();
        const d = pollData?.draft;
        if (d && d.status && d.status !== "analyzing") {
          draft = d;
          break;
        }
      }
      if (!draft) {
        throw new Error("AI-analysen tar längre tid än vanligt. Försök igen om en stund.");
      }
      // 3) Autofill the form from the draft fields. Only fill empty fields
      //    so admin can override before pressing analyze.
      if (!name && draft.company_name) setName(draft.company_name);
      if (!email && draft.lead_email) setEmail(draft.lead_email);
      if (!industry && draft.detected_industry) setIndustry(draft.detected_industry);
      if (!companyUrl && draft.website_url) setCompanyUrl(draft.website_url);
      if (!logoUrl && draft.suggested_logo_url) setLogoUrl(draft.suggested_logo_url);
      if (!allowedDomain && draft.website_url) {
        try {
          const u = new URL(draft.website_url);
          setAllowedDomain(u.hostname.replace(/^www\./, ""));
        } catch { /* ignore */ }
      }
      if (!proactiveMessage && draft.suggested_proactive_message) {
        setProactiveMessage(draft.suggested_proactive_message);
      }
      if (!tone && draft.suggested_tone) setTone(draft.suggested_tone);
      if (!primaryColor && draft.suggested_primary_color) setPrimaryColor(draft.suggested_primary_color);
      if (!startMessage && draft.suggested_start_message) setStartMessage(draft.suggested_start_message);
      const qs = draft.suggested_qualification_fields;
      if (qs && Array.isArray(qs) && qs.length > 0 && !priorityFieldsText) {
        setPriorityFieldsText(qs.join(", "));
      }
      // Build pricing JSON from suggested services + FAQ
      const services = Array.isArray(draft.suggested_services) ? draft.suggested_services : [];
      const faqs = Array.isArray(draft.suggested_faqs) ? draft.suggested_faqs : [];
      if (services.length > 0 || faqs.length > 0) {
        const pricingObj = {
          tone: draft.suggested_tone || "professionellt-vänlig",
          services,
          faq: faqs,
        };
        setPricingText(JSON.stringify(pricingObj, null, 2));
      }
      // v3.44: Auto-enable demo subdoc with AI-suggested content so the
      // public /demo/chatbot/{slug} link works the moment admin saves.
      // No more "I have to click edit-klient just to enable demo" pain.
      setDemoEnabled(true);
      if (!demoHeadline && draft.company_name) {
        setDemoHeadline(`Live-demo: ${draft.company_name}`);
      }
      if (!demoDescription) {
        setDemoDescription(
          "Så här kan er AI-agent se ut på er hemsida. " +
          "Testa hur Elido kan svara på frågor, guida besökare och fånga " +
          "fler förfrågningar direkt från sajten."
        );
      }
      if (!demoTestQuestions) {
        const testQs = Array.isArray(draft.suggested_test_questions) ? draft.suggested_test_questions : [];
        if (testQs.length > 0) {
          setDemoTestQuestions(testQs.slice(0, 8).join("\n"));
        } else if (services.length > 0) {
          // Fallback: derive from services
          setDemoTestQuestions(services.slice(0, 4).map((s) => `Vad kostar det att få hjälp med ${s}?`).join("\n"));
        }
      }
      if (!demoScreenshotUrl && draft.screenshot_url) {
        setDemoScreenshotUrl(draft.screenshot_url);
      }
      setAiState("ok");
    } catch (err) {
      setAiState("error");
      setAiError(err.message || String(err));
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Namn och e-post krävs");
      return;
    }

    // ── Fas 4 — enat skapandeflöde ──────────────────────────────────
    // Om AI-fyll körts skapas kunden via hela kedjan (draft → promote),
    // som bygger Agent Blueprint + föreslagna actions + demo. Granskning,
    // konfiguration och publicering sker sedan i Kund 360. Kunden skapas
    // PAUSAD och aktiveras aldrig automatiskt.
    if (aiDraftId) {
      setSubmitting(true);
      try {
        // Bevara ALLA admins granskade ändringar på utkastet innan promote,
        // i rätt kanonisk struktur (draft.suggested_* + draft.client_config).
        // Fail-close: ogiltig JSON stoppar skapandet och skriver ALDRIG över
        // tjänster/FAQ med tomma listor.
        let services;  // undefined = rör inte fältet
        let faqs;
        if (pricingText && pricingText.trim()) {
          let pj;
          try {
            pj = JSON.parse(pricingText);
          } catch {
            toast.error("Priser/tjänster (JSON) är ogiltig — rätta innan du skapar kunden.");
            setSubmitting(false);
            return;
          }
          if (Array.isArray(pj.services)) services = pj.services;
          if (Array.isArray(pj.faq)) faqs = pj.faq;
        }
        const reviewBody = {
          company_name: name.trim(),
          lead_email: email.trim(),
          industry: industry.trim(),
          website_url: companyUrl.trim() || undefined,
          logo_url: logoUrl.trim() || undefined,
          tone: tone || undefined,
          primary_color: primaryColor || undefined,
          start_message: startMessage || undefined,
          proactive_message: proactiveMessage || undefined,
          screenshot_url: demoScreenshotUrl.trim(),
          qualification_fields: priorityFieldsText
            ? priorityFieldsText.split(",").map((s) => s.trim()).filter(Boolean)
            : undefined,
          test_questions: demoTestQuestions
            ? demoTestQuestions.split("\n").map((s) => s.trim()).filter(Boolean)
            : undefined,
          // Demo- och widgetval bevaras via review→promote (inte best-effort PATCH).
          demo_headline: demoHeadline.trim() || undefined,
          demo_description: demoDescription.trim() || undefined,
          demo_status: demoStatus || "demo",
          demo_sandbox: !!demoSandbox,
          proactive_delay_ms: Number(proactiveDelayMs) || 6000,
          streaming_enabled: !!streamingEnabled,
          streaming_word_delay_ms: Math.max(20, Math.min(400, Number(streamingWordDelayMs) || 160)),
        };
        if (services !== undefined) reviewBody.services = services;
        if (faqs !== undefined) reviewBody.faqs = faqs;
        const rev = await fetch(`${API_URL}/api/admin/onboarding/drafts/${aiDraftId}/review`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify(reviewBody),
        });
        if (!rev.ok) {
          const e = await rev.json().catch(() => ({}));
          throw new Error(e.detail || "Kunde inte spara granskade ändringar");
        }
        // Sätt status=approved så promote släpps igenom.
        await fetch(`${API_URL}/api/admin/onboarding/drafts/${aiDraftId}/status`, {
          method: "PATCH", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
          body: JSON.stringify({ status: "approved" }),
        }).catch(() => {});
        const res = await fetch(`${API_URL}/api/admin/onboarding/drafts/${aiDraftId}/promote`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data.detail || "Kunde inte skapa kund från AI-underlag");
        }
        toast.success(`${name} skapad via AI-underlag · pausad för granskning`);
        onAdded({ client_id: data.client_id, name });
      } catch (err) {
        toast.error(err.message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    let pricing = {};
    try {
      pricing = pricingText.trim() ? JSON.parse(pricingText) : {};
      if (typeof pricing !== "object" || Array.isArray(pricing)) throw new Error("Måste vara ett JSON-objekt");
      setPricingError("");
    } catch (err) {
      setPricingError("Ogiltig JSON: " + err.message);
      return;
    }

    setSubmitting(true);
    try {
      const priorityArr = priorityFieldsText
        .split(/[,\n]+/)
        .map((s) => s.trim().replace(/\s+/g, "_"))
        .filter(Boolean);

      // Demo payload — only sent when admin has toggled it on. Backend
      // auto-fills slug/headline/description if any are blank.
      const demoTestQuestionsArr = demoTestQuestions
        .split(/\n+/)
        .map((q) => q.trim())
        .filter(Boolean)
        .slice(0, 8);
      const demoPayload = demoEnabled
        ? {
            enabled: true,
            sandbox: demoSandbox,
            slug: demoSlug.trim() || null,
            status: demoStatus || "demo",
            headline: demoHeadline.trim() || null,
            description: demoDescription.trim() || null,
            test_questions: demoTestQuestionsArr.length > 0 ? demoTestQuestionsArr : null,
            screenshot_url: demoScreenshotUrl.trim() || null,
          }
        : null;

      const res = await fetch(`${API_URL}/api/admin/client/add`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          pricing,
          industry: industry.trim() || null,
          city: city.trim() || null,
          allowed_domain: allowedDomain.trim() || null,
          company_url: companyUrl.trim() || null,
          logo_url: logoUrl.trim() || null,
          proactive_message: proactiveMessage.trim() || null,
          proactive_delay_ms: proactiveDelayMs.trim() ? Math.max(0, Math.min(120000, parseInt(proactiveDelayMs, 10) || 0)) : null,
          primary_color: primaryColor.trim() || null,
          tone: tone.trim() || null,
          start_message: startMessage.trim() || null,
          streaming_enabled: !!streamingEnabled,
          streaming_word_delay_ms: streamingWordDelayMs.trim()
            ? Math.max(20, Math.min(400, parseInt(streamingWordDelayMs, 10) || 160))
            : 160,
          active,
          lead_qualification_config: priorityArr.length > 0
            ? { priority_fields: priorityArr }
            : {},
          demo: demoPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.detail || "Kunde inte lägga till");
      }
      toast.success(`${name} tillagd${demoPayload ? " · demo aktiverad" : ""}`);
      onAdded(data.client || data.handyman);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="bg-white rounded-xl border border-gray-200 p-6 space-y-4"
      data-testid="add-client-form"
    >
      <h3 className="font-semibold text-gray-900">Skapa kund och AI-chatbot</h3>

      {/* ── AI-autofyll via skrapning ────────────────────────────────────
          Samma pipeline som /skapa-ai-agent. Klistra in en URL,
          tryck "AI-fyll", och fälten nedan fylls automatiskt baserat på
          vad AI:n hittar på hemsidan. Befintlig info överskrivs inte. */}
      <div className="rounded-lg border border-[#3A8A98]/30 bg-gradient-to-br from-[#3A8A98]/5 to-transparent p-4">
        <div className="flex items-start gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#3A8A98] mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-gray-900">AI-fyll från hemsida</div>
            <div className="text-xs text-gray-500">
              Klistra in företagets URL — AI:n skrapar sajten och fyller kundens uppgifter automatiskt.
              Du finjusterar allt efteråt i kundens flikar.
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="url"
            value={aiUrl}
            onChange={(e) => setAiUrl(e.target.value)}
            placeholder="https://foretag.se"
            disabled={aiState === "scraping"}
            data-testid="ai-autofill-url-input"
            className="flex-1"
          />
          <Button
            type="button"
            onClick={runAiAutofill}
            disabled={aiState === "scraping" || !aiUrl.trim()}
            data-testid="ai-autofill-run-btn"
            className="bg-[#255E69] hover:bg-[#3A8A98] text-white"
          >
            {aiState === "scraping" ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> AI analyserar…</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-1.5" /> AI-fyll</>
            )}
          </Button>
        </div>
        {aiState === "ok" && (
          <div className="mt-2 text-xs text-emerald-700" data-testid="ai-autofill-ok">
            ✓ Uppgifter hämtade från AI-analys. Skapa kunden nedan — finjustera sedan i kundens flikar.
          </div>
        )}
        {aiState === "error" && aiError && (
          <div className="mt-2 text-xs text-red-700" data-testid="ai-autofill-error">
            {aiError}
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-gray-600 mb-1 block">Företagsnamn</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Snabba Spiken AB"
            data-testid="client-name-input"
          />
        </div>
        <div>
          <Label className="text-xs text-gray-600 mb-1 block">E-post (leads skickas hit)</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="info@snabbaspiken.se"
            data-testid="client-email-input"
          />
        </div>
      </div>
      <p className="text-xs text-gray-500 -mt-1">
        Övriga fält (domän, utseende, meddelanden, färg, kvalificering och demo) fylls automatiskt av AI-fyll och redigeras efteråt i kundens flikar <span className="font-medium">Widget &amp; utseende</span>, <span className="font-medium">AI-agent &amp; kunskap</span> och <span className="font-medium">Produktdata</span>.
      </p>
      {false && (<>
      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Tillåten domän (valfritt men rekommenderat)</Label>
        <Input
          value={allowedDomain}
          onChange={(e) => setAllowedDomain(e.target.value)}
          placeholder="snabbaspiken.se"
          data-testid="client-allowed-domain-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          Widgeten svarar bara om förfrågan kommer från denna domän (eller subdomäner). Skydd mot spam och missbruk.
          Admin-förhandsvisningen i panelen fungerar alltid.
        </p>
      </div>
      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Klientens hemsida (valfritt)</Label>
        <Input
          value={companyUrl}
          onChange={(e) => setCompanyUrl(e.target.value)}
          placeholder="https://snabbaspiken.se"
          data-testid="client-company-url-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          AI:n läser publik info från sidan (titel, tjänster) som kompletterande bakgrund. Hämtas automatiskt när klienten skapas.
        </p>
      </div>
      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Logga (URL, valfritt)</Label>
        <div className="flex gap-3 items-start">
          <Input
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
            placeholder="https://kund.se/logo.png"
            data-testid="client-logo-url-input"
            className="flex-1"
          />
          {logoUrl.trim() ? (
            <img
              src={logoUrl.trim()}
              alt="logo preview"
              className="h-10 w-10 rounded-full object-cover border border-gray-200 bg-[#2f8590]"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : null}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Visas som avatar i widget-headern. Saknas URL → vit chatt-ikon på teal bakgrund.
        </p>
      </div>
      <div>
        <Label className="text-xs text-gray-600 mb-1 block">
          Prioriterade kvalificeringsfält (komma-separerat, valfritt)
        </Label>
        <Input
          value={priorityFieldsText}
          onChange={(e) => setPriorityFieldsText(e.target.value)}
          placeholder="t.ex. taktyp, storlek, akutgrad, ort, tidsplan"
          data-testid="client-priority-fields-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          AI:n prioriterar dessa fält i konversationen. Lämna tomt för att använda bransch-default
          (eller låta AI:n välja autonomt om bransch saknas).
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs text-gray-600 mb-1 block">Proaktiv hälsning (valfritt)</Label>
          <Input
            value={proactiveMessage}
            onChange={(e) => setProactiveMessage(e.target.value)}
            placeholder="Hej, kan jag hjälpa dig med något?"
            maxLength={140}
            data-testid="client-proactive-message-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Visas som bubbla ovanför launcher-knappen. Lämna tomt för att stänga av.
          </p>
        </div>
        <div>
          <Label className="text-xs text-gray-600 mb-1 block">Fördröjning (ms)</Label>
          <Input
            type="number"
            min="0"
            max="120000"
            step="500"
            value={proactiveDelayMs}
            onChange={(e) => setProactiveDelayMs(e.target.value)}
            placeholder="5000"
            data-testid="client-proactive-delay-input"
          />
          <p className="text-xs text-gray-500 mt-1">5000 = 5 sek (default).</p>
        </div>
      </div>
      <div>
        <Label className="text-xs text-gray-600 mb-1 block">Prislista (JSON)</Label>
        <Textarea
          value={pricingText}
          onChange={(e) => setPricingText(e.target.value)}
          rows={6}
          className="font-mono text-xs"
          data-testid="client-pricing-input"
        />
        {pricingError && <p className="text-xs text-red-600 mt-1">{pricingError}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Tjänster och priser i JSON-format. AI-agenten tränas på dessa när widgeten körs.
        </p>
      </div>

      {/* v3.44: parity med Redigera klient — tonprofil, färg, första
          meddelande, streaming, aktiv-toggle. Tidigare saknades dessa
          helt vid skapande och admin tvingades öppna "Redigera klient"
          direkt efter sparning. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs text-gray-600 mb-1 block">Tonprofil</Label>
          <select
            value={tone}
            onChange={(e) => setTone(e.target.value)}
            className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-[#255E69] focus:outline-none focus:ring-1 focus:ring-[#255E69]"
            data-testid="client-tone-input"
          >
            <option value="">Standard (ingen vald)</option>
            <option value="kort-och-rak">Kort och rak</option>
            <option value="varm-och-personlig">Varm och personlig</option>
            <option value="professionell-och-serviceinriktad">Professionell och serviceinriktad</option>
            <option value="lokal-och-vardaglig">Lokal och vardaglig</option>
            <option value="premium-och-trygg">Premium och trygg</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">Styr AI:ns svarsstil.</p>
        </div>
        <div>
          <Label className="text-xs text-gray-600 mb-1 block">Primärfärg</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : "#255e69"}
              onChange={(e) => setPrimaryColor(e.target.value)}
              className="h-10 w-12 rounded-md border border-gray-200 cursor-pointer bg-white p-1"
              data-testid="client-primary-color-picker"
              aria-label="Välj primärfärg"
            />
            <Input
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#255E69"
              maxLength={20}
              className="flex-1"
              data-testid="client-primary-color-input"
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">Hex (#RRGGBB). Används för launcher + header.</p>
        </div>
        <div>
          <Label className="text-xs text-gray-600 mb-1 block">Första meddelandet</Label>
          <Input
            value={startMessage}
            onChange={(e) => setStartMessage(e.target.value)}
            placeholder="Hej! Vad kan vi hjälpa dig med idag?"
            maxLength={300}
            data-testid="client-start-message-input"
          />
          <p className="text-xs text-gray-500 mt-1">Visas när kunden öppnar chatten.</p>
        </div>
      </div>

      {/* Streaming-läge (samma kontroll som EditClientModal) */}
      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-medium text-gray-800">Streaming-läge</div>
            <p className="text-xs text-gray-500 mt-0.5">
              Av (standard) skickar hela svaret på en gång. På visar svaret ord för ord — premium "skriver-känsla".
            </p>
          </div>
          <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-gray-700 shrink-0">
            <input
              type="checkbox"
              checked={streamingEnabled}
              onChange={(e) => setStreamingEnabled(e.target.checked)}
              className="w-4 h-4 accent-[#255E69]"
              data-testid="client-streaming-enabled"
            />
            <span>Aktivera streaming</span>
          </label>
        </div>
        {streamingEnabled && (
          <div className="mt-3 flex items-end gap-3" data-testid="client-streaming-config">
            <div className="flex-1">
              <Label className="text-xs text-gray-600 mb-1 block">Tid mellan ord (ms)</Label>
              <Input
                type="number"
                min={20}
                max={400}
                step={10}
                value={streamingWordDelayMs}
                onChange={(e) => setStreamingWordDelayMs(e.target.value)}
                placeholder="160"
                data-testid="client-streaming-delay"
              />
              <p className="text-xs text-gray-500 mt-1">
                20–400 ms. Lägre = snabbare. 80 ms = lively, 160 ms = premium, 250 ms = långsamt.
              </p>
            </div>
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-gray-700">
        <input
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="w-4 h-4 accent-[#255E69]"
          data-testid="client-active-input"
        />
        <span>{active ? "Aktiv (widgeten svarar kunder direkt)" : "Pausad (widgeten är avstängd vid skapande)"}</span>
      </label>

      {/* ── Publik demo-sida (auto-aktiveras vid AI-fyll) ──────────────
          Demo-länken /demo/chatbot/<slug> är central för säljflödet —
          därför skapar vi nu demo direkt vid sparning. Tidigare var
          admin tvungen att öppna "Redigera klient" + manuellt kryssa i
          rutan + fylla i fält. */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-[#3A8A98]/5 to-white" data-testid="add-client-demo-section">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <Label className="text-sm font-semibold text-gray-900 mb-0">Publik demo-sida</Label>
            <p className="text-xs text-gray-500 mt-0.5">
              Skapa en personlig demo-länk till prospects via <span className="font-mono">/demo/chatbot/&lt;slug&gt;</span>.
            </p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={demoEnabled}
              onChange={(e) => setDemoEnabled(e.target.checked)}
              data-testid="client-demo-enabled"
              className="w-4 h-4 accent-[#255E69]"
            />
            <span className="text-xs font-medium text-gray-700">{demoEnabled ? "Aktiverad" : "Inaktiv"}</span>
          </label>
        </div>

        {demoEnabled && (
          <div className="space-y-3 pt-3 border-t border-gray-100">
            <label className="flex items-start gap-2 select-none cursor-pointer rounded-lg border border-amber-200 bg-amber-50 p-3">
              <input
                type="checkbox"
                checked={demoSandbox}
                onChange={(e) => setDemoSandbox(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-[#255E69]"
                data-testid="client-demo-sandbox"
              />
              <span>
                <span className="block text-xs font-semibold text-gray-800">Privat demosandbox</span>
                <span className="block text-[11px] text-gray-600 mt-0.5">
                  Kör legacy-runtime, LLM och guards utan att spara leads, konversationer eller skicka mejl.
                </span>
              </span>
            </label>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Status</Label>
              <select
                value={demoStatus}
                onChange={(e) => setDemoStatus(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="client-demo-status"
              >
                <option value="demo">Demo — testar / prospect</option>
                <option value="pilot">Pilot — utvärderar betalt</option>
                <option value="active">Aktiv — betalande kund</option>
                <option value="archived">Arkiverad — inte aktiv längre</option>
              </select>
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Demo-slug (tomt = genereras från företagsnamnet)</Label>
              <Input
                value={demoSlug}
                onChange={(e) => setDemoSlug(e.target.value)}
                placeholder="snabba-spiken-ab"
                data-testid="client-demo-slug"
              />
              {demoSlug && (
                <p className="text-[11px] text-gray-500 mt-1 font-mono break-all">
                  /demo/chatbot/{demoSlug}
                </p>
              )}
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Rubrik (lämna tomt för default)</Label>
              <Input
                value={demoHeadline}
                onChange={(e) => setDemoHeadline(e.target.value)}
                placeholder={`Live-demo: ${name || "{företag}"}`}
                maxLength={160}
                data-testid="client-demo-headline"
              />
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Beskrivning</Label>
              <Textarea
                value={demoDescription}
                onChange={(e) => setDemoDescription(e.target.value)}
                rows={3}
                maxLength={600}
                placeholder="En enkel demo på hur en AI-agent kan fånga upp frågor…"
                data-testid="client-demo-description"
              />
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Testfrågor (en per rad, max 8)</Label>
              <Textarea
                value={demoTestQuestions}
                onChange={(e) => setDemoTestQuestions(e.target.value)}
                rows={4}
                placeholder={"Är det möjligt att få offert?\nVad skulle det kosta att …?\nKan någon kontakta mig?\nHur går det till?"}
                data-testid="client-demo-test-questions"
              />
            </div>

            <div>
              <Label className="text-xs text-gray-600 mb-1 block">
                Bakgrunds-screenshot (frivillig, URL till bild)
              </Label>
              <Input
                type="url"
                value={demoScreenshotUrl}
                onChange={(e) => setDemoScreenshotUrl(e.target.value)}
                placeholder="https://exempel.se/skarmavbild.png"
                data-testid="client-demo-screenshot-url"
              />
              <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                Visas bakom demon för att efterlikna kundens hemsida.
                Lämna tom för standard-gradient.
              </p>
              {demoScreenshotUrl && (
                <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200 bg-slate-900">
                  <img
                    src={demoScreenshotUrl}
                    alt="Demo-bakgrund förhandsvisning"
                    className="w-full h-32 object-cover opacity-60"
                    style={{ filter: "blur(2px)" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid="client-demo-screenshot-preview"
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      </>)}

      <div className="flex gap-2 pt-2">
        <Button type="submit" disabled={submitting} data-testid="client-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Skapa kund och AI-chatbot
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Avbryt
        </Button>
      </div>
    </form>
  );
}

export function EditClientModal({ client, onClose, onSaved }) {
  const { getAuthHeaders } = useAuth();
  const [name, setName] = useState(client.name || "");
  const [email, setEmail] = useState(client.email || "");
  const [industry, setIndustry] = useState(client.industry || "");
  const [city, setCity] = useState(client.city || "");
  const [allowedDomain, setAllowedDomain] = useState(client.allowed_domain || "");
  const [active, setActive] = useState(client.active !== false);
  const [companyUrl, setCompanyUrl] = useState(client.company_url || "");
  const [logoUrl, setLogoUrl] = useState(client.logo_url || "");
  const [scrapeStatus, setScrapeStatus] = useState(client.scraped_context_status || null);
  const [scrapeFetchedAt, setScrapeFetchedAt] = useState(client.scraped_context_fetched_at || null);
  const [rescraping, setRescraping] = useState(false);
  const [priorityFieldsText, setPriorityFieldsText] = useState(() => {
    const cfg = client.lead_qualification_config || {};
    const pf = cfg.priority_fields || [];
    return pf.join(", ");
  });
  const [proactiveMessage, setProactiveMessage] = useState(client.proactive_message || "");
  const [proactiveDelayMs, setProactiveDelayMs] = useState(
    client.proactive_delay_ms !== null && client.proactive_delay_ms !== undefined
      ? String(client.proactive_delay_ms)
      : ""
  );
  const [primaryColor, setPrimaryColor] = useState(client.primary_color || "");
  const [tone, setTone] = useState(client.tone || "");
  const [startMessage, setStartMessage] = useState(client.start_message || "");
  // v3.43: per-client streaming controls. Default is OFF (legacy "whole
  // reply at once" behaviour, which most customers prefer because it
  // feels snappier). When ON, customer sees a word-by-word typewriter
  // animation paced by streamingWordDelayMs.
  const [streamingEnabled, setStreamingEnabled] = useState(!!client.streaming_enabled);
  const [streamingWordDelayMs, setStreamingWordDelayMs] = useState(
    typeof client.streaming_word_delay_ms === "number" && client.streaming_word_delay_ms > 0
      ? String(client.streaming_word_delay_ms)
      : "160"
  );
  const [pricingText, setPricingText] = useState(() => {
    try { return JSON.stringify(client.pricing || {}, null, 2); }
    catch { return "{}"; }
  });
  const [pricingError, setPricingError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ── Demo state (per-client public demo page at /demo/chatbot/:slug) ────
  const _demo0 = client.demo || {};
  const [demoEnabled, setDemoEnabled] = useState(!!_demo0.enabled);
  const [demoSandbox, setDemoSandbox] = useState(!!_demo0.sandbox);
  const [demoSlug, setDemoSlug] = useState(_demo0.slug || "");
  const [demoStatus, setDemoStatus] = useState(_demo0.status || "demo");
  const [demoHeadline, setDemoHeadline] = useState(_demo0.headline || "");
  const [demoDescription, setDemoDescription] = useState(_demo0.description || "");
  const [demoTestQuestions, setDemoTestQuestions] = useState(
    Array.isArray(_demo0.test_questions) && _demo0.test_questions.length > 0
      ? _demo0.test_questions.join("\n")
      : ""
  );
  // Optional background screenshot of the customer's website. Used purely
  // visually on /demo/chatbot/{slug} — falls back to the default gradient
  // when empty. We let admin paste any public image URL; the demo page
  // will render it as a blurred backdrop.
  const [demoScreenshotUrl, setDemoScreenshotUrl] = useState(_demo0.screenshot_url || "");

  // Live JSON validation as the admin types — prevents save-and-break.
  useEffect(() => {
    const trimmed = pricingText.trim();
    if (!trimmed) { setPricingError(""); return; }
    try {
      const parsed = JSON.parse(trimmed);
      if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
        setPricingError("JSON måste vara ett objekt (t.ex. { \"timpris\": 650 }).");
      } else {
        setPricingError("");
      }
    } catch (err) {
      setPricingError("Ogiltig JSON: " + err.message);
    }
  }, [pricingText]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Namn och e-post krävs");
      return;
    }
    let pricing = {};
    try {
      pricing = pricingText.trim() ? JSON.parse(pricingText) : {};
      if (typeof pricing !== "object" || Array.isArray(pricing) || pricing === null) {
        throw new Error("Måste vara ett JSON-objekt");
      }
    } catch (err) {
      setPricingError("Ogiltig JSON: " + err.message);
      toast.error("Prislistan är inte giltig JSON");
      return;
    }
    setSubmitting(true);
    try {
      const priorityArr = priorityFieldsText
        .split(/[,\n]+/)
        .map((s) => s.trim().replace(/\s+/g, "_"))
        .filter(Boolean);

      // Demo payload: only send the demo block if any field was changed —
      // and always include `enabled` so toggling off works.
      const demoTestQuestionsArr = demoTestQuestions
        .split(/\n+/)
        .map((q) => q.trim())
        .filter(Boolean)
        .slice(0, 8);
      const demoPayload = {
        enabled: demoEnabled,
        sandbox: demoSandbox,
        slug: demoSlug.trim() || null,
        status: demoStatus || "demo",
        headline: demoHeadline.trim() || null,
        description: demoDescription.trim() || null,
        test_questions: demoTestQuestionsArr.length > 0 ? demoTestQuestionsArr : null,
        screenshot_url: demoScreenshotUrl.trim() || null,
      };

      const res = await fetch(`${API_URL}/api/admin/client/${client.client_id}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          pricing,
          industry: industry.trim() || null,
          city: city.trim() || null,
          allowed_domain: allowedDomain.trim() || null,
          active,
          company_url: companyUrl.trim() || "",
          logo_url: logoUrl.trim(),
          proactive_message: proactiveMessage.trim(),
          proactive_delay_ms: proactiveDelayMs.trim() ? Math.max(0, Math.min(120000, parseInt(proactiveDelayMs, 10) || 0)) : null,
          primary_color: primaryColor.trim(),
          tone: tone.trim(),
          start_message: startMessage.trim(),
          // v3.43 streaming controls — server clamps the delay to [20, 400] ms.
          streaming_enabled: !!streamingEnabled,
          streaming_word_delay_ms: streamingWordDelayMs.trim()
            ? Math.max(20, Math.min(400, parseInt(streamingWordDelayMs, 10) || 160))
            : 160,
          lead_qualification_config: priorityArr.length > 0
            ? { priority_fields: priorityArr }
            : {},
          demo: demoPayload,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || "Kunde inte spara");
      toast.success("Sparat");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const formatJson = () => {
    try {
      const parsed = JSON.parse(pricingText);
      setPricingText(JSON.stringify(parsed, null, 2));
      setPricingError("");
    } catch (err) {
      setPricingError("Kan inte formatera: " + err.message);
    }
  };

  const triggerRescrape = async () => {
    if (!companyUrl.trim()) {
      toast.error("Ange en URL först");
      return;
    }
    setRescraping(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/client/${client.client_id}/rescrape`, {
        method: "POST",
        credentials: "include",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Hämtade: ${(data.result && data.result.title) || "ok"}`);
        setScrapeStatus("ok");
        setScrapeFetchedAt(new Date().toISOString());
      } else {
        const err = (data.result && data.result.error) || data.detail || "okänt fel";
        toast.error(`Kunde inte hämta: ${err}`);
        setScrapeStatus(err);
      }
    } catch (err) {
      toast.error("Kunde inte hämta");
    } finally {
      setRescraping(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="edit-client-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="text-xs font-semibold text-[#255E69] uppercase tracking-wide flex items-center gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> Redigera klient
            </div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{client.name}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
            data-testid="edit-client-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Företagsnamn</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-client-name" />
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">E-post</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-client-email" />
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Bransch</Label>
              <Input value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="edit-client-industry" />
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Stad</Label>
              <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="edit-client-city" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Tillåten domän</Label>
            <Input
              value={allowedDomain}
              onChange={(e) => setAllowedDomain(e.target.value)}
              placeholder="snabbaspiken.se"
              data-testid="edit-client-allowed-domain"
            />
            <p className="text-xs text-gray-500 mt-1">
              Widgeten svarar bara på anrop från denna domän (eller subdomäner). Lämna tomt för att tillåta alla.
            </p>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Klientens hemsida (valfritt)</Label>
            <div className="flex gap-2">
              <Input
                value={companyUrl}
                onChange={(e) => setCompanyUrl(e.target.value)}
                placeholder="https://snabbaspiken.se"
                data-testid="edit-client-company-url"
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={triggerRescrape}
                disabled={rescraping || !companyUrl.trim()}
                data-testid="edit-client-rescrape-btn"
              >
                {rescraping ? <Loader2 className="w-4 h-4 animate-spin" /> : "Hämta nu"}
              </Button>
            </div>
            <div className="flex items-center justify-between mt-1.5 text-xs">
              {scrapeStatus === "ok" ? (
                <span className="flex items-center gap-1 text-emerald-700">
                  <CheckCircle2 className="w-3 h-3" />
                  Senast hämtad: {scrapeFetchedAt ? new Date(scrapeFetchedAt).toLocaleString("sv-SE") : "—"}
                </span>
              ) : scrapeStatus ? (
                <span className="flex items-center gap-1 text-amber-700">
                  <AlertCircle className="w-3 h-3" />
                  Status: {scrapeStatus}
                </span>
              ) : (
                <span className="text-gray-400">AI:n läser publik info från sidan som kompletterande bakgrund.</span>
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-1 block">Logga (URL, valfritt)</Label>
            <div className="flex gap-3 items-start">
              <Input
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://kund.se/logo.png"
                data-testid="edit-client-logo-url"
                className="flex-1"
              />
              {logoUrl.trim() ? (
                <img
                  src={logoUrl.trim()}
                  alt="logo preview"
                  className="h-10 w-10 rounded-full object-cover border border-gray-200 bg-[#2f8590]"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : null}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Visas som avatar i widget-headern. Saknas URL → vit chatt-ikon på teal bakgrund.
            </p>
          </div>

          <div>
            <Label className="text-xs text-gray-600 mb-1 block">
              Prioriterade kvalificeringsfält (komma-separerat)
            </Label>
            <Input
              value={priorityFieldsText}
              onChange={(e) => setPriorityFieldsText(e.target.value)}
              placeholder="t.ex. taktyp, storlek, akutgrad, ort, tidsplan"
              data-testid="edit-client-priority-fields"
            />
            <p className="text-xs text-gray-500 mt-1">
              AI:n prioriterar att samla in dessa fält i konversationen. Lämna tomt för att använda bransch-default
              (eller låt AI:n välja autonomt om bransch saknas).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-gray-600 mb-1 block">Proaktiv hälsning (valfritt)</Label>
              <Input
                value={proactiveMessage}
                onChange={(e) => setProactiveMessage(e.target.value)}
                placeholder="Hej, kan jag hjälpa dig med något?"
                maxLength={140}
                data-testid="edit-client-proactive-message"
              />
              <p className="text-xs text-gray-500 mt-1">
                Visas som bubbla ovanför launcher-knappen. Lämna tomt för att stänga av.
              </p>
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Fördröjning (ms)</Label>
              <Input
                type="number"
                min="0"
                max="120000"
                step="500"
                value={proactiveDelayMs}
                onChange={(e) => setProactiveDelayMs(e.target.value)}
                placeholder="5000"
                data-testid="edit-client-proactive-delay"
              />
              <p className="text-xs text-gray-500 mt-1">5000 = 5 sek (default).</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Tonprofil</Label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-[#255E69] focus:outline-none focus:ring-1 focus:ring-[#255E69]"
                data-testid="edit-client-tone"
              >
                <option value="">Standard (ingen vald)</option>
                <option value="kort-och-rak">Kort och rak</option>
                <option value="varm-och-personlig">Varm och personlig</option>
                <option value="professionell-och-serviceinriktad">Professionell och serviceinriktad</option>
                <option value="lokal-och-vardaglig">Lokal och vardaglig</option>
                <option value="premium-och-trygg">Premium och trygg</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Styr AI:ns svarsstil.</p>
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Primärfärg</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : "#255e69"}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="h-10 w-12 rounded-md border border-gray-200 cursor-pointer bg-white p-1"
                  data-testid="edit-client-primary-color-picker"
                  aria-label="Välj primärfärg"
                />
                <Input
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#255E69"
                  maxLength={20}
                  className="flex-1"
                  data-testid="edit-client-primary-color"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Hex (#RRGGBB). Används för launcher + header.</p>
            </div>
            <div>
              <Label className="text-xs text-gray-600 mb-1 block">Första meddelandet</Label>
              <Input
                value={startMessage}
                onChange={(e) => setStartMessage(e.target.value)}
                placeholder="Hej! Vad kan vi hjälpa dig med idag?"
                maxLength={300}
                data-testid="edit-client-start-message"
              />
              <p className="text-xs text-gray-500 mt-1">Visas när kunden öppnar chatten.</p>
            </div>
          </div>

          {/* v3.43: per-client streaming controls. Sits between the
              start-message field and the active-toggle so it's adjacent
              to other chat-UI behaviour switches. */}
          <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium text-gray-800">Streaming-läge</div>
                <p className="text-xs text-gray-500 mt-0.5">
                  Av (standard) skickar hela svaret på en gång. På visar svaret ord för ord — premium "skriver-känsla".
                </p>
              </div>
              <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-gray-700 shrink-0">
                <input
                  type="checkbox"
                  checked={streamingEnabled}
                  onChange={(e) => setStreamingEnabled(e.target.checked)}
                  className="w-4 h-4 accent-[#255E69]"
                  data-testid="edit-client-streaming-enabled"
                />
                <span>Aktivera streaming</span>
              </label>
            </div>
            {streamingEnabled && (
              <div className="mt-3 flex items-end gap-3" data-testid="edit-client-streaming-config">
                <div className="flex-1">
                  <Label className="text-xs text-gray-600 mb-1 block">Tid mellan ord (ms)</Label>
                  <Input
                    type="number"
                    min={20}
                    max={400}
                    step={10}
                    value={streamingWordDelayMs}
                    onChange={(e) => setStreamingWordDelayMs(e.target.value)}
                    placeholder="160"
                    data-testid="edit-client-streaming-delay"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    20–400 ms. Lägre = snabbare. 80 ms = lively, 160 ms = premium, 250 ms = långsamt.
                  </p>
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 select-none cursor-pointer text-sm text-gray-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="w-4 h-4 accent-[#255E69]"
              data-testid="edit-client-active"
            />
            <span>{active ? "Aktiv (widgeten svarar kunder)" : "Pausad (widgeten är avstängd)"}</span>
          </label>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs text-gray-600">Prislista (JSON)</Label>
              <button
                type="button"
                onClick={formatJson}
                className="text-xs text-[#255E69] hover:underline disabled:opacity-50"
                disabled={!!pricingError && !pricingText.trim()}
                data-testid="edit-client-format-json"
              >
                Formatera
              </button>
            </div>
            <Textarea
              value={pricingText}
              onChange={(e) => setPricingText(e.target.value)}
              rows={10}
              spellCheck={false}
              className={`font-mono text-xs ${pricingError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
              data-testid="edit-client-pricing"
            />
            {pricingError ? (
              <p className="text-xs text-red-600 mt-1 flex items-center gap-1" data-testid="edit-client-pricing-error">
                <AlertCircle className="w-3 h-3" /> {pricingError}
              </p>
            ) : (
              <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Giltig JSON
              </p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              AI-agenten använder dessa nycklar och priser i sina svar. Ändringar slår igenom direkt på widgeten.
            </p>
          </div>

          {/* ── Klientens livscykel + publik demo-sida ─────────────────── */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gradient-to-br from-[#3A8A98]/5 to-white" data-testid="edit-client-demo-section">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <Label className="text-sm font-semibold text-gray-900 mb-0">Klientens livscykel</Label>
                <p className="text-xs text-gray-500 mt-0.5">Status följer klienten genom säljresan — separat från om demo-sidan är på.</p>
              </div>
            </div>

            {/* Status — alltid synlig så du kan sätta "Aktiv" när kunden köpt
                och samtidigt stänga av publika demo-sidan. */}
            <div className="mb-4">
              <Label className="text-xs text-gray-600 mb-1 block">Status</Label>
              <select
                value={demoStatus}
                onChange={(e) => setDemoStatus(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="edit-client-demo-status"
              >
                <option value="demo">Demo — testar / prospect</option>
                <option value="pilot">Pilot — utvärderar betalt</option>
                <option value="active">Aktiv — betalande kund</option>
                <option value="archived">Arkiverad — inte aktiv längre</option>
              </select>
            </div>

            {/* Public demo page toggle — separate from lifecycle status. */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-200">
              <div>
                <Label className="text-xs font-semibold text-gray-800 mb-0">Publik demo-sida</Label>
                <p className="text-[11px] text-gray-500 mt-0.5">Skicka en personlig demo-länk till prospects via /demo/chatbot/&lt;slug&gt;.</p>
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={demoEnabled}
                  onChange={(e) => setDemoEnabled(e.target.checked)}
                  data-testid="edit-client-demo-enabled"
                  className="w-4 h-4 accent-[#255E69]"
                />
                <span className="text-xs font-medium text-gray-700">{demoEnabled ? "Aktiverad" : "Inaktiv"}</span>
              </label>
            </div>

            {demoEnabled && (
              <div className="space-y-3 mt-3 pt-3 border-t border-gray-100">
                <label className="flex items-start gap-2 select-none cursor-pointer rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <input
                    type="checkbox"
                    checked={demoSandbox}
                    onChange={(e) => setDemoSandbox(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-[#255E69]"
                    data-testid="edit-client-demo-sandbox"
                  />
                  <span>
                    <span className="block text-xs font-semibold text-gray-800">Privat demosandbox</span>
                    <span className="block text-[11px] text-gray-600 mt-0.5">
                      Kör legacy-runtime, LLM och guards utan att spara leads, konversationer eller skicka mejl.
                    </span>
                  </span>
                </label>

                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Demo-slug</Label>
                  <Input
                    value={demoSlug}
                    onChange={(e) => setDemoSlug(e.target.value)}
                    placeholder="genereras från företagsnamnet om tomt"
                    data-testid="edit-client-demo-slug"
                  />
                  {demoSlug && (
                    <p className="text-[11px] text-gray-500 mt-1 font-mono break-all">
                      /demo/chatbot/{demoSlug}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Rubrik (lämna tomt för default)</Label>
                  <Input
                    value={demoHeadline}
                    onChange={(e) => setDemoHeadline(e.target.value)}
                    placeholder={`Demo: AI-agent för ${client.name || "{företag}"}`}
                    maxLength={160}
                    data-testid="edit-client-demo-headline"
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Beskrivning</Label>
                  <Textarea
                    value={demoDescription}
                    onChange={(e) => setDemoDescription(e.target.value)}
                    rows={3}
                    maxLength={600}
                    placeholder="En enkel demo på hur en AI-agent kan fånga upp frågor…"
                    data-testid="edit-client-demo-description"
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">Testfrågor (en per rad, max 8)</Label>
                  <Textarea
                    value={demoTestQuestions}
                    onChange={(e) => setDemoTestQuestions(e.target.value)}
                    rows={4}
                    placeholder={"Är det möjligt att få offert?\nVad skulle det kosta att …?\nKan någon kontakta mig?\nHur går det till?"}
                    data-testid="edit-client-demo-test-questions"
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-600 mb-1 block">
                    Bakgrunds-screenshot (frivillig, URL till bild)
                  </Label>
                  <Input
                    type="url"
                    value={demoScreenshotUrl}
                    onChange={(e) => setDemoScreenshotUrl(e.target.value)}
                    placeholder="https://exempel.se/skarmavbild.png"
                    data-testid="edit-client-demo-screenshot-url"
                  />
                  <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
                    Visas oskärpat bakom demon för att efterlikna kundens hemsida.
                    Lämna tom för standard-gradient.
                  </p>
                  {demoScreenshotUrl && (
                    <div className="mt-2 relative rounded-lg overflow-hidden border border-gray-200 bg-slate-900">
                      <img
                        src={demoScreenshotUrl}
                        alt="Demo-bakgrund förhandsvisning"
                        className="w-full h-32 object-cover opacity-60"
                        style={{ filter: "blur(2px)" }}
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid="edit-client-demo-screenshot-preview"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50">
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Avbryt</Button>
          <Button type="submit" disabled={submitting || !!pricingError} data-testid="edit-client-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Spara
          </Button>
        </div>
      </form>
    </div>
  );
}

export function WidgetPreviewModal({ clientId, clientName, onClose }) {
  const [resetKey, setResetKey] = useState(0);

  // Build a minimal host HTML that loads widget.js from the same origin.
  // srcDoc isolates the host page so widget runs in its real environment.
  const widgetSrc = `${window.location.origin}/widget.js`;
  const previewHtml = `<!doctype html>
<html lang="sv"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>
  html,body{margin:0;padding:0;height:100%;background:linear-gradient(135deg,#f8fafc 0%,#eef2f6 100%);font-family:system-ui,-apple-system,sans-serif}
  .ph{padding:40px 28px;color:#475569}
  .ph h2{margin:0 0 8px 0;color:#0f172a;font-size:18px;font-weight:700}
  .ph p{margin:0;font-size:13px;line-height:1.6;max-width:360px}
  .label{position:absolute;top:16px;left:18px;font-size:10px;color:#94a3b8;letter-spacing:0.1em;text-transform:uppercase;font-weight:600}
</style></head>
<body>
  <span class="label">Sandbox · ${clientName.replace(/[<>"']/g, "")}s sajt</span>
  <div class="ph">
    <h2>Sandbox-läge</h2>
    <p>Hela LLM-flödet körs precis som i produktion — men inga leads sparas och inga mejl skickas. Klicka på chat-knappen nere till höger för att börja testa.</p>
  </div>
  <script>
    // Force a fresh widget session for every preview-open and every reset
    try {
      Object.keys(localStorage).forEach(function(k){
        if (k.indexOf("elido_widget_") === 0) localStorage.removeItem(k);
      });
      Object.keys(sessionStorage).forEach(function(k){
        if (k.indexOf("elido_widget_") === 0 || k.indexOf("elido_proactive_") === 0) sessionStorage.removeItem(k);
      });
    } catch (e) { /* ignore */ }
  </script>
  <script src="${widgetSrc}" data-client-id="${clientId}" data-preview-auth="1" defer></script>
</body></html>`;

  // Close on Escape key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleReset = () => {
    // Bumping resetKey changes the iframe's React key so it unmounts +
    // remounts. The inline script in srcDoc wipes any persisted state on
    // every fresh mount, so the next chat starts from scratch.
    setResetKey((k) => k + 1);
    toast.success("Chatbotten nollställd — börjar om från start");
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="widget-preview-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="text-xs font-semibold text-[#255E69] uppercase tracking-wide flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" /> Sandbox-läge
            </div>
            <div className="text-sm font-semibold text-gray-900 mt-0.5">{clientName}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="px-3 py-1.5 text-xs font-medium text-[#255E69] bg-[#255E69]/10 hover:bg-[#255E69]/20 rounded-lg transition-colors flex items-center gap-1.5"
              data-testid="widget-preview-reset"
              title="Rensa konversationen och börja om"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Nollställ
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-500"
              data-testid="widget-preview-close"
              aria-label="Stäng"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div
          className="px-5 py-2 border-b border-amber-100 bg-amber-50 flex items-center gap-2 text-[11px] text-amber-800"
          data-testid="widget-preview-sandbox-banner"
        >
          <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-amber-500" />
          Sandbox: inga leads sparas, inga mejl skickas. Använd för JSON/prompt-test.
        </div>
        <div className="flex-1 bg-gray-100 relative" style={{ minHeight: "560px" }}>
          <iframe
            key={resetKey}
            title={`Widget preview för ${clientName}`}
            srcDoc={previewHtml}
            className="w-full h-full border-0"
            style={{ minHeight: "560px" }}
            sandbox="allow-scripts allow-same-origin allow-forms"
            data-testid="widget-preview-iframe"
          />
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
          <span>Samma widget som klienten får — men i sandbox-läge</span>
          <span className="font-mono text-[10px] text-gray-400">{clientId}</span>
        </div>
      </div>
    </div>
  );
}

export function WidgetSnippet({ clientId }) {
  const snippet = `<script src="https://elido.nu/widget.js" data-client-id="${clientId}" defer></script>`;
  return (
    <div className="bg-gray-900 text-gray-100 rounded-lg p-4 text-xs font-mono overflow-x-auto">
      <div className="flex items-center justify-between mb-2 text-gray-400 text-[10px] uppercase tracking-wider">
        <span className="flex items-center gap-1.5"><Code2 className="w-3 h-3" /> Widget-kod</span>
        <button
          onClick={async () => {
            await navigator.clipboard.writeText(snippet);
            toast.success("Kod kopierad");
          }}
          className="hover:text-white"
          data-testid="copy-widget-snippet"
        >
          <Copy className="w-3 h-3" />
        </button>
      </div>
      <code className="break-all whitespace-pre-wrap">{snippet}</code>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    partial: { label: "Delvis", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    qualified: { label: "Kvalificerad", cls: "bg-blue-50 text-blue-800 border-blue-200" },
    completed: { label: "Slutförd", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  };
  const m = map[status] || { label: status || "?", cls: "bg-gray-50 text-gray-700 border-gray-200" };
  return (
    <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 border ${m.cls}`} data-testid={`silent-lead-status-${status}`}>
      {m.label}
    </span>
  );
}

export function SilentLeadRow({ lead, clientId, onForwarded }) {
  const { getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const params = lead.extracted_params || {};
  const paramKeys = Object.keys(params);

  const hasContact = !!(lead.customer_email || lead.customer_phone);
  const canForward = (
    (lead.status === "partial" || lead.status === "qualified")
    && hasContact
    && !lead.manual_dispatch
  );

  const handleForceDispatch = async () => {
    const name = lead.customer_name || "denna kund";
    const channel = lead.customer_email
      ? `mejl (${lead.customer_email})`
      : `telefon (${lead.customer_phone})`;
    if (!window.confirm(
      `Skicka ett vidarebefordrat lead-mejl till klienten om ${name}?\n\n` +
      `Kontakt: ${channel}\n\n` +
      `Mejlet får annan formulering än vanliga leads och markerar ` +
      `samtalet som "manuellt vidarebefordrat" i admin.`
    )) {
      return;
    }
    setForwarding(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/client/${clientId}/silent-leads/${lead.session_id}/force-dispatch`,
        {
          method: "POST",
          credentials: "include",
          headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || `Kunde inte skicka lead (status ${res.status})`);
        return;
      }
      if (data.already_dispatched) {
        toast.info("Leadet har redan skickats tidigare.");
      } else {
        toast.success("Lead vidarebefordrat — mejl skickat till klienten.");
      }
      onForwarded?.();
    } catch (e) {
      toast.error("Nätverksfel — kunde inte skicka lead.");
    } finally {
      setForwarding(false);
    }
  };

  return (
    <div className="p-5" data-testid={`silent-lead-row-${lead.session_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={lead.status} />
            {lead.project_type && (
              <span className="text-[11px] font-medium text-gray-700 bg-gray-100 rounded px-2 py-0.5">
                {lead.project_type}
              </span>
            )}
            {lead.intent && lead.intent !== "unknown" && (
              <span className="text-[11px] font-medium text-gray-500">
                {lead.intent}
              </span>
            )}
            {typeof lead.confidence === "number" && lead.confidence > 0 && (
              <span className="text-[10px] text-gray-400" title="AI confidence">
                {Math.round(lead.confidence * 100)}%
              </span>
            )}
          </div>
          <div className="text-sm font-medium text-gray-900 mt-1">
            {lead.customer_name || "Anonym besökare"}
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-0.5">
            {lead.customer_email && (
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {lead.customer_email}</span>
            )}
            {lead.customer_phone && (
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.customer_phone}</span>
            )}
          </div>
        </div>
        <div className="text-xs text-gray-400 whitespace-nowrap">
          {lead.updated_at ? new Date(lead.updated_at).toLocaleString("sv-SE") : ""}
        </div>
      </div>

      {(lead.entry_page_url || lead.page_url) && (
        <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1.5" data-testid={`entry-page-${lead.session_id}`}>
          <Globe className="w-3 h-3 text-slate-400" />
          <span className="text-slate-400">Startade på</span>
          <a
            href={lead.entry_page_url || lead.page_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#255E69] hover:underline truncate max-w-[280px]"
            title={lead.entry_page_url || lead.page_url}
          >
            {(() => {
              try {
                const u = new URL(lead.entry_page_url || lead.page_url);
                return u.pathname + (u.search || "") || u.hostname;
              } catch (e) {
                return lead.entry_page_url || lead.page_url;
              }
            })()}
          </a>
          {Array.isArray(lead.journey) && lead.journey.length > 1 && (
            <span className="text-slate-400">+ {lead.journey.length - 1} sid{lead.journey.length - 1 === 1 ? "a" : "or"} till</span>
          )}
        </div>
      )}

      {paramKeys.length > 0 && (
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 bg-gray-50 rounded-lg p-3" data-testid="silent-lead-params">
          {paramKeys.map((k) => (
            <div key={k} className="text-xs">
              <div className="text-gray-500 uppercase tracking-wide text-[10px]">{k}</div>
              <div className="text-gray-900 font-medium truncate">{String(params[k])}</div>
            </div>
          ))}
        </div>
      )}

      {(lead.missing_params && lead.missing_params.length > 0) && (
        <div className="mt-2 text-xs text-gray-500">
          Saknas: {lead.missing_params.join(", ")}
        </div>
      )}

      {(lead.skipped_params && lead.skipped_params.length > 0) && (
        <div className="mt-1 text-xs text-amber-700">
          Skippade: {lead.skipped_params.join(", ")}
        </div>
      )}

      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gray-400 uppercase tracking-wider">
        {lead.qualification_source && (
          <span>källa: {lead.qualification_source.replace("_", " ")}</span>
        )}
        {typeof lead.asked_params_count === "number" && lead.asked_params_count > 0 && (
          <span>{lead.asked_params_count} frågor ställda</span>
        )}
      </div>

      <div className="mt-2 flex items-center gap-3 flex-wrap">
        <button
          onClick={() => setOpen((s) => !s)}
          className="inline-flex items-center gap-1 text-xs text-[#255E69] hover:underline"
          data-testid={`silent-lead-expand-${lead.session_id}`}
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
          {open ? "Dölj" : "Visa"} konversation
        </button>

        {canForward && (
          <button
            type="button"
            onClick={handleForceDispatch}
            disabled={forwarding}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-900 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-full px-3 py-1 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid={`silent-lead-force-dispatch-${lead.session_id}`}
            title="Skicka lead-mejl manuellt med annorlunda formulering"
          >
            {forwarding
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Send className="w-3.5 h-3.5" />}
            {forwarding ? "Skickar..." : "Skicka som lead"}
          </button>
        )}

        {lead.manual_dispatch && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
            data-testid={`silent-lead-manual-badge-${lead.session_id}`}
          >
            <CheckCircle2 className="w-3 h-3" /> Manuellt skickat
          </span>
        )}
      </div>
      {open && (
        <CustomerJourney lead={lead} />
      )}
      {open && (
        <div className="mt-2 bg-white border border-gray-200 rounded-lg p-3 space-y-1.5 max-h-64 overflow-y-auto">
          {(lead.conversation_history || []).map((m, i) => (
            <div key={i} className="text-xs leading-relaxed">
              <span className={`font-semibold ${m.role === "user" ? "text-[#255E69]" : "text-gray-500"}`}>
                {m.role === "user" ? "Kund" : "AI"}:
              </span>{" "}
              <span className="text-gray-800 whitespace-pre-wrap">{m.content}</span>
            </div>
          ))}
          {(!lead.conversation_history || lead.conversation_history.length === 0) && (
            <div className="text-xs text-gray-400">Ingen sparad konversation.</div>
          )}
        </div>
      )}
    </div>
  );
}

// Customer journey: shows the entry page, referrer, and a tidy timeline of
// pages the customer visited during the chat session.
export function CustomerJourney({ lead }) {
  const journey = Array.isArray(lead.journey) ? lead.journey : [];
  const entryUrl = lead.entry_page_url || lead.page_url || (journey[0] && journey[0].url);
  const entryTitle = lead.entry_page_title || (journey[0] && journey[0].title);
  const referrer = lead.referrer;
  const fmtPath = (u) => {
    if (!u) return "";
    try {
      const parsed = new URL(u);
      return parsed.pathname + (parsed.search || "");
    } catch (e) {
      return u;
    }
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" });
    } catch (e) {
      return "";
    }
  };
  const hasAny = journey.length > 0 || entryUrl || referrer;
  if (!hasAny) return null;

  return (
    <div
      className="mt-3 bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-lg p-3"
      data-testid={`customer-journey-${lead.session_id}`}
    >
      <div className="flex items-center gap-1.5 mb-2 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        <MapPin className="w-3 h-3" /> Kundresa
      </div>

      <div className="space-y-1.5 text-xs">
        {entryUrl && (
          <div className="flex items-start gap-2">
            <span className="text-slate-400 shrink-0">Startade på:</span>
            <a
              href={entryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#255E69] hover:underline break-all font-medium"
              title={entryUrl}
            >
              {entryTitle ? `${entryTitle} (${fmtPath(entryUrl)})` : fmtPath(entryUrl)}
            </a>
          </div>
        )}

        {referrer && (
          <div className="flex items-start gap-2">
            <span className="text-slate-400 shrink-0">Refererad från:</span>
            <span className="text-slate-700 break-all">{referrer}</span>
          </div>
        )}
      </div>

      {journey.length > 1 && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">
            Sidor under chatten ({journey.length})
          </div>
          <ol className="relative border-l border-slate-200 ml-1.5 space-y-1.5">
            {journey.map((p, idx) => (
              <li key={idx} className="pl-3 -ml-px relative">
                <span className="absolute -left-[5px] top-1.5 w-2 h-2 rounded-full bg-[#3A8A98] border-2 border-white" />
                <div className="flex items-baseline gap-2 flex-wrap">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-slate-800 hover:text-[#255E69] hover:underline break-all"
                    title={p.url}
                  >
                    {p.title ? `${p.title} — ${fmtPath(p.url)}` : fmtPath(p.url)}
                  </a>
                  {p.at && (
                    <span className="text-[10px] text-slate-400 shrink-0">{fmtTime(p.at)}</span>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export function SilentLeadsList({ clientId }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [loadingMore, setLoadingMore] = useState(false);

  const PAGE_SIZE = 50;

  const fetchPage = useCallback(async (offset) => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));
    if (filter !== "all") params.set("status", filter);
    const url = `${API_URL}/api/admin/client/${clientId}/silent-leads?${params.toString()}`;
    const res = await fetch(url, { credentials: "include", headers: getAuthHeaders() });
    if (!res.ok) throw new Error(`Status ${res.status}`);
    return res.json();
  }, [clientId, filter, getAuthHeaders]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchPage(0));
    } catch (e) {
      toast.error("Kunde inte läsa in silent leads");
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    try {
      const nextOffset = (data.leads || []).length;
      const more = await fetchPage(nextOffset);
      setData((prev) => ({
        ...more,
        leads: [...(prev?.leads || []), ...(more.leads || [])],
      }));
    } catch (e) {
      toast.error("Kunde inte hämta fler leads");
    } finally {
      setLoadingMore(false);
    }
  }, [data, loadingMore, fetchPage]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!data) return null;

  const leads = data.leads || [];
  const counts = data.counts || { partial: 0, qualified: 0, completed: 0 };
  const pagination = data.pagination || {};
  const hasMore = !!pagination.has_more;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap" data-testid="silent-leads-filter">
          {[
            { id: "all", label: "Alla", count: counts.partial + counts.qualified + counts.completed },
            { id: "partial", label: "Delvis", count: counts.partial },
            { id: "qualified", label: "Kvalificerade", count: counts.qualified },
            { id: "completed", label: "Slutförda", count: counts.completed },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                filter === f.id
                  ? "bg-[#255E69] text-white border-[#255E69]"
                  : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
              }`}
              data-testid={`silent-leads-filter-${f.id}`}
            >
              {f.label} <span className="opacity-60">({f.count})</span>
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="silent-leads-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {leads.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Activity className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Inga {filter === "all" ? "" : filter + " "}silent leads än</h3>
          <p className="text-sm text-gray-500">När kunder börjar chatta med widgeten dyker partial leads upp här i realtid.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
            {leads.map((l) => <SilentLeadRow key={l.session_id} lead={l} clientId={clientId} onForwarded={load} />)}
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500" data-testid="silent-leads-pagination">
            <span>
              Visar {leads.length} av {pagination.total ?? leads.length}
            </span>
            {hasMore && (
              <Button
                variant="outline"
                size="sm"
                onClick={loadMore}
                disabled={loadingMore}
                data-testid="silent-leads-load-more"
              >
                {loadingMore ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                Visa fler
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}


export default function ClientsManager() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  // Kundfas-filter (demo.status) och Widgetstatus-filter.
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [widgetFilter, setWidgetFilter] = useState("all");

  // En enda, sammanhängande listläsning (arbetsorder §4). Vid fel visas
  // ett verkligt felläge med "Försök igen" — inga tyst bortfallna badges.
  const loadClients = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setClients(data.clients || []);
    } catch (e) {
      setError(true);
      toast.error("Kunde inte läsa in kunder");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const handleCreated = (created) => {
    setShowAdd(false);
    const cid = created?.client_id;
    loadClients();
    if (cid) {
      // Navigera med API-svarets riktiga client_id till Kund 360, Översikt.
      navigate(`/admin/clients/${encodeURIComponent(cid)}?tab=overview`);
    }
  };

  const openClient = (cid) =>
    navigate(`/admin/clients/${encodeURIComponent(cid)}?tab=overview`);

  const q = search.trim().toLowerCase();
  const visibleClients = clients.filter((c) => {
    if (q) {
      const hay = `${c.name || ""} ${c.email || ""} ${c.company_url || ""} ${c.allowed_domain || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (phaseFilter !== "all") {
      const phase = c.demo_status || "_unset";
      if (phase !== phaseFilter) return false;
    }
    if (widgetFilter === "on" && !c.active) return false;
    if (widgetFilter === "paused" && c.active) return false;
    return true;
  });

  const PHASE_OPTIONS = [
    { key: "all", label: "Alla faser" },
    { key: "demo", label: "Demo" },
    { key: "pilot", label: "Pilot" },
    { key: "active", label: "Kund" },
    { key: "archived", label: "Arkiverad" },
    { key: "_unset", label: "Ej angiven" },
  ];
  const WIDGET_OPTIONS = [
    { key: "all", label: "Widget: alla" },
    { key: "on", label: "Widget På" },
    { key: "paused", label: "Widget Pausad" },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="clients-manager">
      {/* ── Rubrik + åtgärder ─────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <Users className="w-6 h-6 text-[#255E69]" />
          <h1 data-testid="clients-heading">Kunder</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadClients} data-testid="clients-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Uppdatera
          </Button>
          <Button onClick={() => setShowAdd(true)} data-testid="clients-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Ny kund
          </Button>
        </div>
      </div>

      {/* ── Sök + filter ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök på namn, domän eller e-post"
            className="pl-9"
            data-testid="clients-search-input"
          />
        </div>
        <select
          value={phaseFilter}
          onChange={(e) => setPhaseFilter(e.target.value)}
          data-testid="clients-phase-filter"
          className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#255E69]/30"
        >
          {PHASE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <select
          value={widgetFilter}
          onChange={(e) => setWidgetFilter(e.target.value)}
          data-testid="clients-widget-filter"
          className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#255E69]/30"
        >
          {WIDGET_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {/* ── Ny kund-dialog (separat skapandeflöde) ────────────────────── */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="add-client-dialog">
          <DialogHeader>
            <DialogTitle>Skapa kund och AI-chatbot</DialogTitle>
            <DialogDescription>
              Ange namn, e-post och hemsida. AI-fyll hämtar övriga fält från kundens sajt och sätter upp chatboten automatiskt.
            </DialogDescription>
          </DialogHeader>
          <AddClientForm onAdded={handleCreated} onCancel={() => setShowAdd(false)} />
        </DialogContent>
      </Dialog>

      {/* ── Innehåll ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="bg-white rounded-xl border border-red-200 p-10 text-center" data-testid="clients-error">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Kunde inte läsa in kunder</h3>
          <p className="text-sm text-gray-500 mb-4">Statusinformationen kunde inte hämtas.</p>
          <Button onClick={loadClients} data-testid="clients-retry-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Försök igen
          </Button>
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <h3 className="font-semibold text-gray-900 mb-1">Inga kunder än</h3>
          <p className="text-sm text-gray-500 mb-4">Lägg till din första kund för att aktivera deras AI-agent.</p>
          <Button onClick={() => setShowAdd(true)}><Plus className="w-4 h-4 mr-1" /> Ny kund</Button>
        </div>
      ) : visibleClients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center" data-testid="clients-filter-empty">
          <p className="text-sm text-gray-500">Inga kunder matchar sökningen eller filtret.</p>
          <button
            type="button"
            onClick={() => { setSearch(""); setPhaseFilter("all"); setWidgetFilter("all"); }}
            className="mt-3 text-xs font-medium text-[#255E69] hover:underline"
          >
            Rensa filter
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
          {visibleClients.map((c) => {
            const phase = kundfas(c.demo_status);
            const widget = widgetStatus(c.active);
            const rt = runtimeState(c.active_runtime, c.runtime_health);
            const domain = c.company_url || c.allowed_domain || c.email || "";
            const count = c.completed_requests_count ?? c.leads_count ?? 0;
            return (
              <div
                key={c.client_id}
                className="p-4 sm:p-5 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4"
                data-testid={`client-row-${c.client_id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={() => openClient(c.client_id)}
                      className="font-semibold text-gray-900 break-words hover:text-[#255E69] hover:underline text-left"
                      data-testid={`client-name-${c.client_id}`}
                    >
                      {c.name || "(namnlös)"}
                    </button>
                    <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 shrink-0 border ${phase.cls}`}
                          data-testid={`phase-badge-${c.client_id}`}>
                      {phase.label}
                    </span>
                    <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 shrink-0 border ${widget.cls}`}
                          data-testid={`widget-badge-${c.client_id}`}>
                      {widget.label}
                    </span>
                    <span className={`text-[10px] uppercase font-semibold tracking-wider rounded-full px-2 py-0.5 shrink-0 border ${rt.cls}`}
                          data-testid={`runtime-badge-${c.client_id}`}>
                      {rt.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1.5">
                    <span className="flex items-center gap-1 min-w-0 max-w-full">
                      <Globe className="w-3 h-3 shrink-0" />
                      <span className="truncate">{domain || "—"}</span>
                    </span>
                    <span>· {count} förfråg{count === 1 ? "an" : "ningar"}</span>
                  </div>
                </div>
                <div className="flex items-center justify-end shrink-0">
                  <Button
                    size="sm"
                    onClick={() => openClient(c.client_id)}
                    data-testid={`open-client-${c.client_id}`}
                    className="bg-[#0F2A30] hover:bg-[#255E69] text-white"
                  >
                    <span className="hidden sm:inline">Öppna kund</span>
                    <span className="sm:hidden text-xs font-medium">Öppna</span>
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
